import React, { useMemo, useState, useCallback, useEffect, memo, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  horizontalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  bulkUploadDndAutoScroll,
  bulkUploadDndMeasuring,
  useBulkUploadDndSensors,
} from '../utils/bulkUploadDndConfig';
import { toast } from 'react-toastify';
import Toast from '../../Toast';
import { BulkUploadWizardShell } from './BulkUploadWizardShell';
import GeneralPopup from '../../GeneralPopup';
import helpIcon from '../../../static/img/helpgray24.svg';
import warningIcon from '../../../static/icons/Warning-icon.svg';
import pagesAssignedArt from '../../../static/icons/robot-win.svg';
import bentArrowIcon from '../../../static/icons/bent-arrow-icon.svg';
import pagesEmptyIcon from '../../../static/img/bulk-pages-empty.svg';
import arrowDown from '../../../static/img/arrowdown2.svg';
import arrowUp from '../../../static/img/arrowup2.svg';
import zoomIcon from '../../../static/icons/black-magnifying-icon.svg';
import profileCircle from '../../../static/img/profile-circle.svg';
import { Avatar } from '@boringer-avatars/react';
import QuestionTooltip from '../../../components2/QuestionTooltip';
import StyledDropDown from '../../../components2/StyledDropDown';
import { htmlToPlainText } from '../utils/html';
import {
  BulkUploadAllAssigned,
  BulkUploadAllAssignedArrow,
  BulkUploadAllAssignedArt,
  BulkUploadAllAssignedText,
  BulkUploadAllAssignedTitle,
  BulkUploadAddFilesButton,
  BulkUploadAddStudentLink,
  BulkUploadAddStudentDropdown,
  BulkUploadAddStudentDropdownHeader,
  BulkUploadAddStudentDropdownSubtitle,
  BulkUploadAddStudentDropdownTitle,
  BulkUploadAddStudentDropdownWrapper,
  BulkUploadAssignButton,
  BulkUploadAssignSection,
  BulkUploadDragHandle,
  BulkUploadInstructionList,
  BulkUploadInstructionItem,
  BulkUploadOrganiseLayout,
  BulkUploadOrganisePagesGrid,
  BulkUploadOrganisePagesMain,
  BulkUploadPageCard,
  BulkUploadPageDeleteOverlay,
  BulkUploadPageImage,
  BulkUploadManualPagePreview,
  BulkUploadPageNumberOverlay,
  BulkUploadPageOverlay,
  BulkUploadPagePreview,
  BulkUploadPagePreviewHoverOverlay,
  BulkUploadPagePreviewIcon,
  BulkUploadPageTitle,
  BulkUploadSectionTitle,
  BulkUploadSelectButton,
  BulkUploadStudentAvatar,
  BulkUploadStudentCardActions,
  BulkUploadStudentCardIcon,
  BulkUploadMoreDots,
  BulkUploadStudentCardRow,
  BulkUploadStudentInfo,
  BulkUploadStudentList,
  BulkUploadStudentListPanel,
  BulkUploadStudentListHeader,
  BulkUploadStudentName,
  BulkUploadStudentPages,
  BulkUploadStudentCard,
  BulkUploadStudentThumb,
  BulkUploadStudentThumbBody,
  BulkUploadStudentThumbHandle,
  BulkUploadStudentThumbHoverOverlay,
  BulkUploadStudentThumbNumber,
  BulkUploadStudentThumbRemove,
  BulkUploadStudentThumbs,
} from '../styles';
import { BulkUploadPageGallery } from './BulkUploadPageGallery';
import { BulkUploadClassStudentFilter } from './BulkUploadClassStudentFilter';
import { flattenClassStudents } from '../utils/students';
import { renumberPages } from '../utils/draftPageSnapshot';
import { isPlaceholderThumbnail } from '../utils/thumbnails';

import styled from 'styled-components';
const BulkUploadMultiQuestionNotice = styled.div`
  padding: 12px 16px;
  border-radius: 12px;
  border: 1.5px solid rgba(114, 0, 224, 0.3);
  background: rgba(241, 230, 252, 0.15);
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
  font-size: 14px;
  line-height: 20px;
  color: #4b464f;

  strong {
    font-weight: 600;
    color: #1e252a;
  }
`;

// Deduplicate by page ID to prevent duplicate key errors. Kept separate from the
// numbering: this screen is the one that can produce duplicates, and "pageNumber
// is array position" is a rule the whole wizard shares.
// First occurrence wins, matching the filter-based version this replaced — where
// two pages share an id the earlier one is the one already rendered.
const uniqueById = (pages) => {
  const seen = new Set();
  return (pages || []).filter((page) => !seen.has(page.id) && seen.add(page.id));
};

const renumberUniquePages = (pages) => renumberPages(uniqueById(pages));

// Named counts, so the teacher sees the size of what they are about to leave behind.
/**
 * The routing with an empty slot for every student who does not have one yet, or
 * the SAME object when they all do.
 *
 * Returning `current` unchanged is the point. The roster arrives as an
 * equal-but-new array on every parent render, so building a new object regardless
 * changed studentAssignments, which the upward sync reported, which re-rendered
 * the parent, which handed down another fresh roster — an endless cycle. It cost
 * more than frames: the wizard autosaves that report on an 800ms debounce, and
 * every render re-armed the timer, so it never fired and the routing never
 * reached the server.
 */
const withEmptySlotsFor = (students, current) => {
  const missing = students.filter((student) => !current[student.id]);
  if (!missing.length) return current;
  return missing.reduce(
    (next, student) => ({ ...next, [student.id]: [] }),
    current,
  );
};

const unassignedWarningText = (count) =>
  `${count} ${count === 1 ? 'page is' : 'pages are'} not assigned to a student. ` +
  'Those pages will not be uploaded and will not be marked.';

const STUDENT_LIST_HELP_TEXT =
  'Names in student list can be anything, not just student names';

const HorizontalGripIcon = () => (
  <svg width="16" height="10" viewBox="0 0 16 10" fill="none" aria-hidden>
    <circle cx="2" cy="2" r="1.5" fill="currentColor" />
    <circle cx="8" cy="2" r="1.5" fill="currentColor" />
    <circle cx="14" cy="2" r="1.5" fill="currentColor" />
    <circle cx="2" cy="8" r="1.5" fill="currentColor" />
    <circle cx="8" cy="8" r="1.5" fill="currentColor" />
    <circle cx="14" cy="8" r="1.5" fill="currentColor" />
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const XIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ThumbGripIcon = () => (
  <svg width="12" height="8" viewBox="0 0 12 8" fill="none" aria-hidden>
    <circle cx="2" cy="2" r="1.1" fill="currentColor" />
    <circle cx="6" cy="2" r="1.1" fill="currentColor" />
    <circle cx="10" cy="2" r="1.1" fill="currentColor" />
    <circle cx="2" cy="6" r="1.1" fill="currentColor" />
    <circle cx="6" cy="6" r="1.1" fill="currentColor" />
    <circle cx="10" cy="6" r="1.1" fill="currentColor" />
  </svg>
);

const PageCardVisual = ({
  page,
  isAssignedToStudent = false,
  isDragging = false,
  isOverlay = false,
  isDisabled = false,
  style,
  setNodeRef,
  dragHandleProps,
  onDelete,
  onClick,
  onPreview,
}) => {
  const [cursorPosition, setCursorPosition] = useState({ x: 50, y: 50 });

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setCursorPosition({ x, y });
  };

  return (
    <BulkUploadPageCard
      ref={setNodeRef}
      style={style}
      $organise
      $highlighted={isAssignedToStudent}
      $isDragging={isDragging}
      $isOverlay={isOverlay}
      $disabled={isDisabled}
    >
      <BulkUploadDragHandle
        style={{ cursor: isDragging || isOverlay ? 'grabbing' : 'grab', touchAction: 'none' }}
        onClick={(e) => e.stopPropagation()}
        {...dragHandleProps}
      >
        <HorizontalGripIcon />
      </BulkUploadDragHandle>
      {page.label && <BulkUploadPageTitle>{page.label}</BulkUploadPageTitle>}
      <BulkUploadPagePreview $organise>
        {page.source === 'manual' && page.content ? (
          <BulkUploadManualPagePreview>{htmlToPlainText(page.content)}</BulkUploadManualPagePreview>
        ) : (
          <BulkUploadPageImage
            src={page.thumbnail || page.fileUrl}
            alt={`Page ${page.pageNumber}`}
            draggable={false}
          />
        )}
        {!isOverlay && onPreview && (
          <BulkUploadPagePreviewHoverOverlay
            aria-hidden="true"
            onMouseMove={handleMouseMove}
          >
            <BulkUploadPagePreviewIcon
              type="button"
              aria-label={`Preview page ${page.pageNumber}`}
              tabIndex={0}
              $posX={cursorPosition.x}
              $posY={cursorPosition.y}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onPreview(page.id);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  onPreview(page.id);
                }
              }}
            >
              <img src={zoomIcon} alt="" width={20} height={20} />
            </BulkUploadPagePreviewIcon>
          </BulkUploadPagePreviewHoverOverlay>
        )}
        <BulkUploadPageOverlay>
          <BulkUploadPageNumberOverlay>{page.pageNumber}</BulkUploadPageNumberOverlay>
          {!isOverlay && onDelete && page?.id && (
            <BulkUploadPageDeleteOverlay
              type="button"
              aria-label={`Delete page ${page.pageNumber}`}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onDelete(page.id);
              }}
            >
              <img src="/icons/delete-logo.svg" alt="" width={14} height={14} />
            </BulkUploadPageDeleteOverlay>
          )}
        </BulkUploadPageOverlay>
      </BulkUploadPagePreview>
      {!isOverlay && (
        <BulkUploadSelectButton
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onClick?.(e);
          }}
          disabled={isDisabled}
          $deselect={isAssignedToStudent}
        >
          {isDisabled ? 'Assigned' : (isAssignedToStudent ? <XIcon /> : <CheckIcon />)}
          {isDisabled ? '' : (isAssignedToStudent ? 'Deselect' : 'Select')}
        </BulkUploadSelectButton>
      )}
    </BulkUploadPageCard>
  );
};

const SortablePageCard = memo(({
  page,
  isAssignedToStudent,
  isDragActive,
  isDisabled,
  onDelete,
  onTogglePage,
  onPreview,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `main-${page.id}`, disabled: Boolean(isDisabled) });

  return (
    <PageCardVisual
      page={page}
      isAssignedToStudent={isAssignedToStudent}
      isDragging={isDragging}
      isDisabled={isDisabled}
      setNodeRef={setNodeRef}
      style={{
        transform: CSS.Translate.toString(transform),
        transition: isDragging || isDragActive ? undefined : transition,
      }}
      dragHandleProps={{
        ref: setActivatorNodeRef,
        ...attributes,
        ...listeners,
      }}
      onDelete={onDelete}
      onClick={() => onTogglePage(page.id)}
      onPreview={onPreview}
    />
  );
});

const StudentThumbVisual = ({
  page,
  studentId,
  isDragging = false,
  isOverlay = false,
  style,
  setNodeRef,
  dragHandleProps,
  onOpenGallery,
  onRemove,
}) => (
  <BulkUploadStudentThumb
    ref={setNodeRef}
    style={style}
    $isDragging={isDragging}
    $isOverlay={isOverlay}
    role="button"
    tabIndex={isOverlay ? -1 : 0}
    aria-label={`Preview page ${page.pageNumber}`}
    onClick={(e) => {
      if (isOverlay) return;
      e.stopPropagation();
      onOpenGallery?.(studentId, page.id);
    }}
    onKeyDown={(e) => {
      if (isOverlay) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        onOpenGallery?.(studentId, page.id);
      }
    }}
  >
    <BulkUploadStudentThumbHandle
      style={{ cursor: isDragging || isOverlay ? 'grabbing' : 'grab' }}
      {...dragHandleProps}
      onClick={(e) => e.stopPropagation()}
    >
      <ThumbGripIcon />
    </BulkUploadStudentThumbHandle>
    <BulkUploadStudentThumbBody>
      {page.source === 'manual' && page.content ? (
        <span
          style={{
            display: 'block',
            padding: 4,
            fontSize: 7,
            lineHeight: 1.2,
            color: '#4b464f',
            overflow: 'hidden',
            height: '100%',
          }}
        >
          {htmlToPlainText(page.content)}
        </span>
      ) : (
        <img src={page.thumbnail || page.fileUrl} alt="" draggable={false} />
      )}
      <BulkUploadStudentThumbHoverOverlay aria-hidden="true">
        <img src={zoomIcon} alt="" width={12} height={12} />
      </BulkUploadStudentThumbHoverOverlay>
      <BulkUploadStudentThumbNumber>{page.pageNumber}</BulkUploadStudentThumbNumber>
    </BulkUploadStudentThumbBody>
    {!isOverlay && onRemove && (
      <BulkUploadStudentThumbRemove
        type="button"
        aria-label={`Remove page ${page.pageNumber}`}
        onClick={(e) => {
          e.stopPropagation();
          onRemove(studentId, page.id);
        }}
      >
        <img src="/icons/red_close.svg" alt="" width={14} height={14} />
      </BulkUploadStudentThumbRemove>
    )}
  </BulkUploadStudentThumb>
);

const SortableStudentThumb = memo(({
  page,
  studentId,
  isDragActive,
  onOpenGallery,
  onRemove,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `student-${studentId}-${page.id}` });

  return (
    <StudentThumbVisual
      page={page}
      studentId={studentId}
      isDragging={isDragging}
      setNodeRef={setNodeRef}
      style={{
        transform: CSS.Translate.toString(transform),
        transition: isDragging || isDragActive ? undefined : transition,
      }}
      dragHandleProps={{
        ref: setActivatorNodeRef,
        ...attributes,
        ...listeners,
      }}
      onOpenGallery={onOpenGallery}
      onRemove={onRemove}
    />
  );
});

const StudentThumbList = memo(({
  studentId,
  pages,
  isDragActive,
  onOpenGallery,
  onRemove,
}) => {
  const pageIds = useMemo(
    () => pages.map((page) => `student-${studentId}-${page.id}`),
    [pages, studentId]
  );

  // Use a container ID prefixed with "student-" to identify drop zones
  const containerId = `student-${studentId}`;

  return (
    <SortableContext
      items={pageIds}
      strategy={horizontalListSortingStrategy}
      id={containerId}
    >
      <BulkUploadStudentThumbs data-student-id={studentId}>
        {pages.map((page) => (
          <SortableStudentThumb
            key={page.id}
            page={page}
            studentId={studentId}
            isDragActive={isDragActive}
            onOpenGallery={onOpenGallery}
            onRemove={onRemove}
          />
        ))}
      </BulkUploadStudentThumbs>
    </SortableContext>
  );
});

// Droppable student card row wrapper - allows dropping pages onto student rows
const DroppableStudentCardRow = ({
  studentId,
  isExpanded,
  isActive,
  name,
  pageNumbers,
  studentPages,
  onToggleExpand,
  onOpenGallery,
  onRemove,
  isDragActive,
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `student-dropzone-${studentId}`,
    data: {
      type: 'student-row',
      studentId: studentId,
    },
  });

  return (
    <BulkUploadStudentCard
      key={studentId}
      $active={isActive}
      ref={setNodeRef}
      $isDropTarget={isOver && isDragActive}
    >
      <BulkUploadStudentCardRow
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        style={{ cursor: 'pointer' }}
        onClick={onToggleExpand}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggleExpand();
          }
        }}
      >
        <div style={{ width: '24px', height: '24px', flexShrink: 0, borderRadius: '50%', overflow: 'hidden' }}>
          <Avatar
            title={false}
            size={24}
            variant="pixel"
            name={name}
            square={false}
          />
        </div>
        <BulkUploadStudentInfo>
          <BulkUploadStudentName>{name}</BulkUploadStudentName>
          {studentPages.length > 0 && (
            <BulkUploadStudentPages>
              Pages: <strong>{studentPages.length}</strong>
            </BulkUploadStudentPages>
          )}
        </BulkUploadStudentInfo>
        <BulkUploadStudentCardActions>
          <BulkUploadStudentCardIcon aria-hidden="true">
            <img
              src={isExpanded ? arrowUp : arrowDown}
              alt=""
              width={16}
              height={16}
            />
          </BulkUploadStudentCardIcon>
        </BulkUploadStudentCardActions>
      </BulkUploadStudentCardRow>
      {isExpanded && studentPages.length > 0 && (
        <StudentThumbList
          studentId={studentId}
          pages={studentPages}
          isDragActive={isDragActive}
          onOpenGallery={onOpenGallery}
          onRemove={onRemove}
        />
      )}
    </BulkUploadStudentCard>
  );
};

export const BulkUploadOrganisePagesStep = ({
  steps,
  currentStep = 3,
  mode = 'schoolTeacher',
  students = [],
  classes = [],
  initialPages = [],
  assignments = {},
  onBack,
  onClose,
  onContinue,
  onStepClick,
  onAssignmentsChange,
  isContinueDisabled = false,
  fullPage = false,
  isLoadingStudents = false,
  // The number of answerable questions on a class task, when there is more than
  // one. Routing stays per student; the notice says questions are split at Review.
  multiQuestionCount = 0,
}) => {
  const isSchoolTeacher = mode === 'schoolTeacher';
  const [selectedClassIds, setSelectedClassIds] = useState([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [extraStudents, setExtraStudents] = useState([]);
  const [newStudentName, setNewStudentName] = useState('');
  const [showHeaderAddStudent, setShowHeaderAddStudent] = useState(false);
  const [showAddStudentDropdown, setShowAddStudentDropdown] = useState(false);

  const rosterStudents = useMemo(() => {
    if (!isSchoolTeacher) {
      return Array.isArray(students) ? students : [];
    }
    if (Array.isArray(students) && students.length > 0) {
      return students;
    }
    const fromClasses = flattenClassStudents(
      selectedClassIds.length
        ? (classes || []).filter((clazz) => selectedClassIds.includes(clazz.id))
        : classes,
    );
    return fromClasses.length ? fromClasses : students || [];
  }, [students, classes, selectedClassIds, isSchoolTeacher]);

  const safeStudents = useMemo(() => {
    const base = [...rosterStudents, ...extraStudents];

    let result;
    if (!selectedStudentIds.length) {
      result = base;
    } else {
      const selected = new Set(selectedStudentIds.map(String));
      const extras = extraStudents.filter((s) => !selected.has(String(s.id)));
      result = [
        ...base.filter((s) => selected.has(String(s.id))),
        ...extras,
      ];
    }

    // Deduplicate by student ID at the end to prevent dropdown key errors
    const seenIds = new Set();
    return result.filter((student) => {
      const id = String(student.id);
      if (seenIds.has(id)) {
        return false;
      }
      seenIds.add(id);
      return true;
    });
  }, [rosterStudents, extraStudents, selectedStudentIds]);

  const safePages = Array.isArray(initialPages) ? initialPages : [];

  const [pages, setPages] = useState(() => renumberUniquePages(safePages));
  const [selectedStudentId, setSelectedStudentId] = useState(safeStudents[0]?.id || null);

  // Track the last initialPages to detect when it changes (e.g., when coming back from DragDrop step)
  const lastInitialPagesRef = useRef(initialPages);
  const [expandedStudentId, setExpandedStudentId] = useState(null);
  const [galleryPageId, setGalleryPageId] = useState(null);
  const [galleryStudentId, setGalleryStudentId] = useState(null);
  const [previewPageId, setPreviewPageId] = useState(null);
  const [activePageId, setActivePageId] = useState(null);
  const [activeStudentThumbId, setActiveStudentThumbId] = useState(null);
  const [pendingPageIds, setPendingPageIds] = useState([]);
  const [showUnassignedWarning, setShowUnassignedWarning] = useState(false);
  const [studentAssignments, setStudentAssignments] = useState(() => {
    // Use passed assignments if they exist, otherwise initialize empty
    if (assignments && Object.keys(assignments).length > 0) {
      // Deduplicate any existing duplicate page IDs
      const deduplicated = {};
      Object.keys(assignments).forEach((studentId) => {
        deduplicated[studentId] = [...new Set(assignments[studentId] || [])];
      });
      return deduplicated;
    }
    return safeStudents.reduce((acc, student) => {
      acc[student.id] = [];
      return acc;
    }, {});
  });

  // One-time cleanup: deduplicate any existing duplicate assignments
  const hasDeduplicatedRef = useRef(false);
  useEffect(() => {
    if (hasDeduplicatedRef.current) return;
    hasDeduplicatedRef.current = true;

    setStudentAssignments((current) => {
      let hasChanges = false;
      const deduplicated = {};

      Object.keys(current).forEach((studentId) => {
        const pageIds = current[studentId] || [];
        const uniqueIds = [...new Set(pageIds)];
        deduplicated[studentId] = uniqueIds;
        if (uniqueIds.length !== pageIds.length) {
          hasChanges = true;
        }
      });

      return hasChanges ? deduplicated : current;
    });
  }, []);

  useEffect(() => {
    if (!safeStudents.length) return;

    // Initialize student assignments for new students. Unchanged when there are
    // none — see withEmptySlotsFor for why that matters.
    setStudentAssignments((current) => withEmptySlotsFor(safeStudents, current));

    // Only update selectedStudentId if current one is invalid or null
    // Don't reset it unnecessarily when safeStudents array reference changes
    setSelectedStudentId((current) => {
      // If current is valid, keep it - don't reset
      if (
        current &&
        safeStudents.some((s) => String(s.id) === String(current))
      ) {
        return current;
      }
      // Only set to first student if current is null/invalid
      return safeStudents[0]?.id || null;
    });
  }, [safeStudents]);

  // Sync pages when initialPages changes (e.g., when coming back from DragDrop step)
  // Use a separate ref to track page IDs to prevent feedback loops
  const lastPageIdsRef = useRef(safePages.map((page) => page.id).join(','));

  useEffect(() => {
    // Only update if initialPages actually changed (not just reference)
    if (lastInitialPagesRef.current === initialPages) return;

    // Same IDs/order may still carry thumbnails that finished after assignment.
    const newPageIds = (Array.isArray(initialPages) ? initialPages : []).map(p => p.id).join(',');

    if (lastPageIdsRef.current === newPageIds && newPageIds) {
      lastInitialPagesRef.current = initialPages;
      const incoming = new Map(initialPages.map((page) => [page.id, page.thumbnail]));
      setPages((current) => {
        let changed = false;
        const merged = current.map((page) => {
          const thumbnail = incoming.get(page.id);
          if (isPlaceholderThumbnail(thumbnail) || thumbnail === page.thumbnail) return page;
          changed = true;
          return { ...page, thumbnail };
        });
        // Map current pages so a late preview cannot undo a local reorder or
        // deletion. Keep the array when unchanged so upward sync settles.
        return changed ? merged : current;
      });
      return;
    }

    lastInitialPagesRef.current = initialPages;
    lastPageIdsRef.current = newPageIds;
    const newPages = renumberUniquePages(Array.isArray(initialPages) ? initialPages : []);
    setPages(newPages);
  }, [initialPages]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!showAddStudentDropdown) return;

    const handleClickOutside = (event) => {
      const dropdown = event.target.closest('[data-add-student-dropdown]');
      if (!dropdown) {
        setShowAddStudentDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAddStudentDropdown]);

  // Sync pages and assignments changes back to parent immediately
  // Use refs to track previous values and only sync when there are actual changes
  const prevPagesRef = useRef(pages);
  const prevAssignmentsRef = useRef(studentAssignments);
  const prevStudentIdsRef = useRef('');
  const onAssignmentsChangeRef = useRef(onAssignmentsChange);

  // Keep the callback ref up to date
  useEffect(() => {
    onAssignmentsChangeRef.current = onAssignmentsChange;
  }, [onAssignmentsChange]);

  useEffect(() => {
    if (!onAssignmentsChangeRef.current) return;

    const pagesChanged = prevPagesRef.current !== pages;
    const assignmentsChanged = prevAssignmentsRef.current !== studentAssignments;

    // Check if student IDs actually changed (not just array reference)
    const currentStudentIds = safeStudents.map(s => s.id).join(',');
    const studentsChanged = prevStudentIdsRef.current !== currentStudentIds;

    if (pagesChanged || assignmentsChanged || studentsChanged) {
      prevPagesRef.current = pages;
      prevAssignmentsRef.current = studentAssignments;
      prevStudentIdsRef.current = currentStudentIds;

      onAssignmentsChangeRef.current({
        assignments: studentAssignments,
        pages,
        students: safeStudents
      });
    }
  }, [pages, studentAssignments]);

  const handleAddStudent = () => {
    const name = newStudentName.trim();
    if (!name) return;
    const id = `temp_${uuidv4()}`;
    setExtraStudents((current) => [...current, { id, name, studentName: name }]);
    setSelectedStudentId(id);
    setExpandedStudentId(id);
    setNewStudentName('');
    setShowHeaderAddStudent(false);
  };

  const sensors = useBulkUploadDndSensors();

  const activeDragPage = useMemo(() => {
    if (!activePageId) return null;

    // Extract actual page ID from prefixed sortable ID
    // Format: "main-{pageId}"
    const id = String(activePageId);
    const pageId = id.startsWith('main-') ? id.replace('main-', '') : id;

    return pages.find((page) => page.id === pageId);
  }, [activePageId, pages]);

  const handleReorderPages = useCallback((event) => {
    const { active, over } = event;
    if (!over) return;

    // Extract actual page ID from prefixed sortable ID
    // Format: "main-{pageId}" or "student-{studentId}-{pageId}"
    const activeId = String(active.id);
    let pageId;
    if (activeId.startsWith('main-')) {
      pageId = activeId.replace('main-', '');
    } else if (activeId.startsWith('student-')) {
      // Extract pageId from "student-{studentId}-{pageId}"
      const parts = activeId.split('-');
      pageId = parts.slice(2).join('-'); // Handle page IDs that contain dashes
    } else {
      pageId = activeId;
    }

    // Check if dragging to a student container
    const overData = over?.data?.current;
    let targetStudentId = overData?.sortable?.containerId;

    // Also check if dropped directly on a student thumbnail area
    if (!targetStudentId) {
      // Try to find student ID from the over element's data attributes
      const overElement = document.querySelector(`[data-student-id]`);
      if (overElement) {
        targetStudentId = `student-${overElement.getAttribute('data-student-id')}`;
      }
    }

    if (targetStudentId && targetStudentId.startsWith('student-')) {
      // Dragging from main grid to student thumbnail area - assign the page
      const studentId = targetStudentId.replace('student-', '');
      assignPageToStudent(pageId, studentId);
      return;
    }

    // Regular reordering within main grid
    if (active.id === over.id) return;

    // Extract page IDs from prefixed sortable IDs
    const overId = String(over.id);
    let overPageId;
    if (overId.startsWith('main-')) {
      overPageId = overId.replace('main-', '');
    } else if (overId.startsWith('student-')) {
      const parts = overId.split('-');
      overPageId = parts.slice(2).join('-');
    } else {
      overPageId = overId;
    }

    setPages((current) => {
      const oldIndex = current.findIndex((page) => page.id === pageId);
      const newIndex = current.findIndex((page) => page.id === overPageId);
      if (oldIndex < 0 || newIndex < 0) return current;
      return renumberUniquePages(arrayMove(current, oldIndex, newIndex));
    });
  }, [studentAssignments, safeStudents]);

  const handleDeletePage = useCallback((pageId) => {
    if (!pageId) return;

    // Update local state
    const updatedPages = renumberUniquePages(pages.filter((p) => p.id !== pageId));
    const updatedAssignments = { ...studentAssignments };
    Object.keys(updatedAssignments).forEach((studentId) => {
      updatedAssignments[studentId] = (updatedAssignments[studentId] || []).filter((id) => id !== pageId);
    });

    setPages(updatedPages);
    setStudentAssignments(updatedAssignments);

    // Notify parent to sync deletion back to DragDrop step
    if (onAssignmentsChange) {
      onAssignmentsChange({
        assignments: updatedAssignments,
        pages: updatedPages,
        students: safeStudents,
      });
    }
  }, [pages, studentAssignments, safeStudents, onAssignmentsChange]);

  const handleTogglePageForStudent = useCallback((pageId) => {
    setPendingPageIds((current) => {
      if (current.includes(pageId)) {
        return current.filter((id) => id !== pageId);
      }
      return [...current, pageId];
    });
  }, []);

  const handleAssignPages = useCallback(() => {
    if (!selectedStudentId || pendingPageIds.length === 0) return;

    // Check if any pending pages are already assigned to other students
    const conflicts = [];
    pendingPageIds.forEach((pageId) => {
      const assignedToOtherStudent = Object.entries(studentAssignments).find(
        ([studentId, pages]) => studentId !== selectedStudentId && pages.includes(pageId)
      );
      if (assignedToOtherStudent) {
        const [otherStudentId] = assignedToOtherStudent;
        const otherStudent = safeStudents.find(
          (s) => String(s.id) === String(otherStudentId),
        );
        const studentName = otherStudent?.name || otherStudent?.studentName || 'another student';
        conflicts.push({ pageId, studentName });
      }
    });

    if (conflicts.length > 0) {
      const pageWord = conflicts.length === 1 ? 'page is' : 'pages are';
      toast(
        <Toast
          message={`${conflicts.length} ${pageWord} already assigned to other students. Each page can only be assigned to one student.`}
        />
      );
      return;
    }

    // Assign all pending pages to the selected student (ensure no duplicates)
    setStudentAssignments((current) => {
      const existingIds = current[selectedStudentId] || [];
      const allIds = [...existingIds, ...pendingPageIds];
      const uniqueIds = [...new Set(allIds)];
      return {
        ...current,
        [selectedStudentId]: uniqueIds,
      };
    });

    // Clear pending selection
    setPendingPageIds([]);
  }, [selectedStudentId, pendingPageIds, studentAssignments, safeStudents]);


  const handleUnassignPage = useCallback((studentId, pageId) => {
    setStudentAssignments((current) => ({
      ...current,
      [studentId]: (current[studentId] || []).filter((id) => id !== pageId),
    }));
  }, []);

  const handleReorderStudentPages = useCallback((studentId, { active, over }) => {
    if (!over || active.id === over.id) return;

    // Extract page IDs from prefixed sortable IDs
    // Format: "student-{studentId}-{pageId}"
    const extractPageId = (prefixedId) => {
      const id = String(prefixedId);
      if (id.startsWith('student-')) {
        const parts = id.split('-');
        return parts.slice(2).join('-'); // Handle page IDs that contain dashes
      }
      return id;
    };

    const activePageId = extractPageId(active.id);
    const overPageId = extractPageId(over.id);

    setStudentAssignments((current) => {
      const ids = current[studentId] || [];
      const oldIndex = ids.indexOf(activePageId);
      const newIndex = ids.indexOf(overPageId);
      if (oldIndex < 0 || newIndex < 0) return current;
      return {
        ...current,
        [studentId]: arrayMove(ids, oldIndex, newIndex),
      };
    });
  }, []);

  const getStudentPageNumbers = (studentId) => {
    const pageIds = studentAssignments[studentId] || [];
    return pageIds
      .map((id) => pages.find((p) => p.id === id)?.pageNumber)
      .filter(Boolean);
  };

  const getStudentPages = (studentId) =>
    (studentAssignments[studentId] || [])
      .map((id) => pages.find((p) => p.id === id))
      .filter(Boolean);

  const galleryPages = useMemo(() => {
    if (!galleryPageId || !galleryStudentId) return [];
    return getStudentPages(galleryStudentId);
  }, [galleryPageId, galleryStudentId, studentAssignments, pages]);

  const openGallery = useCallback((studentId, pageId) => {
    setGalleryStudentId(studentId);
    setGalleryPageId(pageId);
  }, []);

  const closeGallery = useCallback(() => {
    setGalleryPageId(null);
    setGalleryStudentId(null);
  }, []);

  const openPagePreview = useCallback((pageId) => {
    setPreviewPageId(pageId);
  }, []);

  const closePagePreview = useCallback(() => {
    setPreviewPageId(null);
  }, []);

  const previewPages = useMemo(() => {
    if (!previewPageId) return [];
    const page = pages.find((p) => p.id === previewPageId);
    return page ? [page] : [];
  }, [previewPageId, pages]);

  // Auto-close preview if page is deleted
  useEffect(() => {
    if (!previewPageId) return;
    const pageStillExists = pages.some((p) => p.id === previewPageId);
    if (!pageStillExists) {
      setPreviewPageId(null);
    }
  }, [previewPageId, pages]);

  const assignedPageIds = useMemo(() => {
    const ids = new Set();
    Object.values(studentAssignments).forEach((list) => {
      (list || []).forEach((id) => ids.add(id));
    });
    return ids;
  }, [studentAssignments]);

  const unassignedPages = useMemo(
    () => pages.filter((page) => !assignedPageIds.has(page.id)),
    [pages, assignedPageIds],
  );
  const unassignedPageIds = useMemo(
    () => unassignedPages.map((page) => `main-${page.id}`),
    [unassignedPages],
  );

  const allPagesAssigned =
    pages.length > 0 && unassignedPages.length === 0;

  // Check if at least one page is assigned to a student
  const hasAnyAssignments = assignedPageIds.size > 0;

  useEffect(() => {
    if (!allPagesAssigned) return;
    const firstWithPages = safeStudents.find(
      (student) => (studentAssignments[student.id] || []).length > 0,
    );
    if (!firstWithPages) return;
    setExpandedStudentId(firstWithPages.id);
    setSelectedStudentId(firstWithPages.id);
    // Only when the empty-state becomes active — avoid re-forcing expand on later edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPagesAssigned]);

  const commitAssignments = () => {
    // FE-BU10: Pass the final routing through the callback instead of relying
    // on state having landed. Both onAssignmentsChange and onContinue run in the
    // same tick, so the closure onContinue reads from would hold stale values.
    const finalAssignments = { ...studentAssignments };
    const finalStudents = [...safeStudents];

    if (onAssignmentsChange) {
      onAssignmentsChange({ assignments: finalAssignments, pages, students: finalStudents });
    }
    // Pass the final routing through so the commit reads fresh values, not stale closure
    onContinue?.({ assignments: finalAssignments, pages, students: finalStudents });
  };

  const handleContinue = () => {
    // This step is the commit point: only assigned pages reach the batch, so an
    // unassigned page is dropped for good. Make the teacher confirm that loss.
    if (unassignedPages.length > 0) {
      setShowUnassignedWarning(true);
      return;
    }
    commitAssignments();
  };

  const confirmDropUnassigned = () => {
    setShowUnassignedWarning(false);
    commitAssignments();
  };

  // Test data attributes
  const assignedPagesCount = Array.from(assignedPageIds).length;
  const unassignedPagesCount = pages.length - assignedPagesCount;
  const studentsWithPagesCount = Object.values(studentAssignments).filter(pages => pages.length > 0).length;

  // Helper to extract page ID from prefixed sortable IDs
  const extractPageId = useCallback((prefixedId) => {
    const id = String(prefixedId);
    if (id.startsWith('main-')) {
      return id.replace('main-', '');
    } else if (id.startsWith('student-')) {
      const parts = id.split('-');
      return parts.slice(2).join('-'); // Handle page IDs that contain dashes
    }
    return id;
  }, []);

  // Assign a page to a student (with reassignment support)
  const assignPageToStudent = useCallback((pageId, targetStudentId) => {
    // Check if already assigned to THIS student (no-op)
    if (studentAssignments[targetStudentId]?.includes(pageId)) {
      return; // Silent no-op
    }

    // Check if assigned to ANOTHER student (reassignment)
    const currentStudentId = Object.keys(studentAssignments).find(
      sid => studentAssignments[sid]?.includes(pageId)
    );

    setStudentAssignments((current) => {
      const updated = { ...current };

      // Remove from old student if exists
      if (currentStudentId) {
        updated[currentStudentId] = updated[currentStudentId].filter(id => id !== pageId);
      }

      // Add to new student (avoid duplicates)
      const existingIds = updated[targetStudentId] || [];
      updated[targetStudentId] = existingIds.includes(pageId)
        ? existingIds
        : [...existingIds, pageId];

      return updated;
    });

    // Auto-expand target student to show assignment
    setExpandedStudentId(targetStudentId);
  }, [studentAssignments, safeStudents]);

  // Unified drag handler for both main pages and student thumbnails
  const handleDragStart = useCallback(({ active }) => {
    // Check the container ID to determine if dragging from main grid or student thumbnails
    const containerId = active.data?.current?.sortable?.containerId;
    const isStudentThumb = containerId && containerId.startsWith('student-');

    if (isStudentThumb) {
      setActiveStudentThumbId(active.id);
    } else {
      setActivePageId(active.id);
    }
  }, []);

  const handleDragCancel = useCallback(() => {
    setActivePageId(null);
    setActiveStudentThumbId(null);
  }, []);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;

    // Check if it's a student thumbnail drag
    const isThumbDrag = activeStudentThumbId === active.id;

    // Always clear active states first to prevent stale state
    setActivePageId(null);
    setActiveStudentThumbId(null);

    if (!over) return;

    // Check if dropped on a student row dropzone
    if (over.id && String(over.id).startsWith('student-dropzone-')) {
      const targetStudentId = String(over.id).replace('student-dropzone-', '');
      const pageId = extractPageId(active.id);
      assignPageToStudent(pageId, targetStudentId);
      return;
    }

    if (isThumbDrag) {
      // Student thumbnail reordering
      if (active.id === over.id) return;

      // Extract page ID from prefixed sortable ID
      // Format: "student-{studentId}-{pageId}"
      const activeId = String(active.id);
      let pageId;
      if (activeId.startsWith('student-')) {
        const parts = activeId.split('-');
        pageId = parts.slice(2).join('-'); // Handle page IDs that contain dashes
      } else {
        pageId = activeId;
      }

      // Find which student this thumbnail belongs to
      let targetStudentId = null;
      Object.entries(studentAssignments).forEach(([studentId, pageIds]) => {
        if (pageIds.includes(pageId)) {
          targetStudentId = studentId;
        }
      });

      if (!targetStudentId) return;
      handleReorderStudentPages(targetStudentId, event);
    } else {
      // Main page drag
      handleReorderPages(event);
    }
  }, [activeStudentThumbId, studentAssignments, handleReorderPages, handleReorderStudentPages, extractPageId, assignPageToStudent]);

  return (
    <BulkUploadWizardShell
      steps={steps}
      currentStep={currentStep}
      onBack={onBack}
      onClose={onClose}
      onContinue={handleContinue}
      onStepClick={onStepClick}
      continueLabel="Save and Continue"
      isContinueDisabled={isContinueDisabled || pages.length === 0 || !hasAnyAssignments}
      fullPage={fullPage}
      data-component="bulk-upload-organise"
      data-unit="bulk-upload-step-3"
      data-total-pages={pages.length}
      data-total-students={safeStudents.length}
      data-assigned-pages={assignedPagesCount}
      data-unassigned-pages={unassignedPagesCount}
      data-students-with-pages={studentsWithPagesCount}
      data-selected-student-id={selectedStudentId}
      data-pending-pages={pendingPageIds.length}
      data-all-assigned={allPagesAssigned}
      data-continue-disabled={isContinueDisabled || pages.length === 0 || !hasAnyAssignments}
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        measuring={bulkUploadDndMeasuring}
        autoScroll={bulkUploadDndAutoScroll}
        onDragStart={handleDragStart}
        onDragCancel={handleDragCancel}
        onDragEnd={handleDragEnd}
      >
        <BulkUploadOrganiseLayout>
        <BulkUploadOrganisePagesMain>
          <BulkUploadSectionTitle>Organise pages</BulkUploadSectionTitle>
          {multiQuestionCount > 1 && (
            <BulkUploadMultiQuestionNotice data-testid="organise-multi-question-notice">
              <strong>This task has {multiQuestionCount} questions.</strong> Route each
              student's whole paper to them here, pages in reading order. At Review you
              will see where each question starts and ends in the text and can move the
              split, so a page that holds two questions is fine.
            </BulkUploadMultiQuestionNotice>
          )}

          {pages.length > 0 && !allPagesAssigned && (
            <BulkUploadInstructionList>
              <BulkUploadInstructionItem>Add student names first using the "+ Add student" button</BulkUploadInstructionItem>
              <BulkUploadInstructionItem>You can select multiple pages before assigning by clicking "Select"</BulkUploadInstructionItem>
              <BulkUploadInstructionItem>Use the dropdown to assign selected pages to a student together</BulkUploadInstructionItem>
              <BulkUploadInstructionItem>Pages can be drag-and-dropped to reorder within the main grid or student list</BulkUploadInstructionItem>
            </BulkUploadInstructionList>
          )}

          {pages.length === 0 ? (
            <BulkUploadAllAssigned>
              <BulkUploadAllAssignedArt
                src={pagesEmptyIcon}
                alt=""
                width={120}
                height={120}
              />
              <BulkUploadAllAssignedTitle>
                All pages have been deleted
              </BulkUploadAllAssignedTitle>
              <BulkUploadAllAssignedText>
                Go back to the previous step to add pages
              </BulkUploadAllAssignedText>
              <BulkUploadAddFilesButton
                type="button"
                $outline
                onClick={onBack}
                style={{ height: 40, padding: '8px 24px', marginTop: 4 }}
              >
                Back
              </BulkUploadAddFilesButton>
            </BulkUploadAllAssigned>
          ) : (
            <>
              {allPagesAssigned ? (
                <BulkUploadAllAssigned>
                  <BulkUploadAllAssignedArt
                    src={pagesAssignedArt}
                    alt=""
                    width={170}
                    height={220}
                  />
                  <BulkUploadAllAssignedTitle>
                    All pages have been assigned
                  </BulkUploadAllAssignedTitle>
                  <BulkUploadAllAssignedText>
                    You can reorder pages in the student list if you want
                  </BulkUploadAllAssignedText>
                  <BulkUploadAllAssignedArrow>
                    <img src={bentArrowIcon} alt="" />
                  </BulkUploadAllAssignedArrow>
                </BulkUploadAllAssigned>
              ) : (
                <BulkUploadAssignSection>
                  <BulkUploadSectionTitle style={{ margin: 0, fontSize: '16px' }}>
                    <img src={profileCircle} alt="" width={20} height={20} />
                    Assign page(s) to
                  </BulkUploadSectionTitle>
                  <StyledDropDown
                    menuItems={safeStudents.map((student) => ({
                      title: student.studentName || student.name || 'Unknown Student',
                      id: student.id,
                    }))}
                    selectedIndex={safeStudents.findIndex(
                      (s) => String(s.id) === String(selectedStudentId),
                    )}
                    onItemSelected={(item) => setSelectedStudentId(item.id)}
                    placeholder="Select student"
                    fullWidth
                    highlightedItems={safeStudents
                      .filter(
                        (student) =>
                          (studentAssignments[student.id] || []).length > 0,
                      )
                      .map((student) => student.id)}
                  />
                  <QuestionTooltip
                    text="Select one or more pages to assign them"
                    disabled={pendingPageIds.length > 0}
                  >
                    <span style={{ display: 'inline-block', width: '100%' }}>
                      <BulkUploadAssignButton
                        type="button"
                        onClick={handleAssignPages}
                        disabled={pendingPageIds.length === 0}
                        data-testid="bulk-upload-assign-button"
                        data-disabled={pendingPageIds.length === 0}
                        data-pending-pages={pendingPageIds.length}
                      >
                        Assign
                      </BulkUploadAssignButton>
                    </span>
                  </QuestionTooltip>
                </BulkUploadAssignSection>
              )}

              {/* Only unassigned pages live here so IDs never clash with student thumbs */}
              <SortableContext
                items={unassignedPageIds}
                strategy={rectSortingStrategy}
                id="main-pages"
              >
                <BulkUploadOrganisePagesGrid
                  style={{
                    display: allPagesAssigned ? 'none' : undefined,
                  }}
                >
                  {unassignedPages.map((page) => {
                    const isPending = pendingPageIds.includes(page.id);

                    return (
                      <SortablePageCard
                        key={page.id}
                        page={page}
                        isAssignedToStudent={isPending}
                        isDragActive={!!activePageId}
                        onDelete={handleDeletePage}
                        onTogglePage={handleTogglePageForStudent}
                        onPreview={openPagePreview}
                      />
                    );
                  })}
                </BulkUploadOrganisePagesGrid>
              </SortableContext>
            </>
          )}
        </BulkUploadOrganisePagesMain>

        <BulkUploadStudentListPanel>
          <BulkUploadStudentListHeader>
            <BulkUploadSectionTitle $plain>Student list</BulkUploadSectionTitle>
            <QuestionTooltip text={STUDENT_LIST_HELP_TEXT}>
              <span style={{ display: 'inline-flex', lineHeight: 0 }}>
                <img src={helpIcon} alt="" width={20} height={20} aria-hidden="true" />
              </span>
            </QuestionTooltip>
            {isSchoolTeacher && classes.length > 0 ? (
              <BulkUploadAddStudentDropdownWrapper data-add-student-dropdown>
                <BulkUploadAddStudentLink
                  type="button"
                  onClick={() => setShowAddStudentDropdown(!showAddStudentDropdown)}
                >
                  + Add student
                </BulkUploadAddStudentLink>
                {showAddStudentDropdown && (
                  <BulkUploadAddStudentDropdown>
                    <BulkUploadAddStudentDropdownHeader>
                      <BulkUploadAddStudentDropdownTitle>
                        Add students
                      </BulkUploadAddStudentDropdownTitle>
                      <BulkUploadAddStudentDropdownSubtitle>
                        Select classes and individual students
                      </BulkUploadAddStudentDropdownSubtitle>
                    </BulkUploadAddStudentDropdownHeader>
                    <BulkUploadClassStudentFilter
                      classes={classes}
                      selectedClassIds={selectedClassIds}
                      selectedStudentIds={selectedStudentIds}
                      onClassIdsChange={setSelectedClassIds}
                      onStudentIdsChange={setSelectedStudentIds}
                    />
                  </BulkUploadAddStudentDropdown>
                )}
              </BulkUploadAddStudentDropdownWrapper>
            ) : !isSchoolTeacher ? (
              <BulkUploadAddStudentLink
                type="button"
                onClick={() => setShowHeaderAddStudent((open) => !open)}
              >
                + Add student
              </BulkUploadAddStudentLink>
            ) : null}
          </BulkUploadStudentListHeader>

          {!isSchoolTeacher && showHeaderAddStudent && (
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                autoFocus
                value={newStudentName}
                onChange={(e) => setNewStudentName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAddStudent();
                }}
                placeholder="Student name"
                style={{
                  flex: 1,
                  height: 36,
                  borderRadius: 8,
                  border: '1px solid rgba(201,198,204,0.85)',
                  padding: '8px 10px',
                  boxSizing: 'border-box',
                  fontSize: 14,
                  fontFamily: "var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif)",
                }}
              />
              <BulkUploadAssignButton
                type="button"
                onClick={handleAddStudent}
                disabled={!newStudentName.trim()}
                style={{ width: 'auto', whiteSpace: 'nowrap', height: 36 }}
              >
                Add
              </BulkUploadAssignButton>
            </div>
          )}

          <BulkUploadStudentList>
            {safeStudents.map((student, index) => {
              const pageNumbers = getStudentPageNumbers(student.id);
              const studentPages = getStudentPages(student.id);
              const name = student.studentName || student.name || 'Unknown Student';
              const isActive =
                String(student.id) === String(selectedStudentId);
              const isExpanded =
                String(student.id) === String(expandedStudentId);

              return (
                <DroppableStudentCardRow
                  key={student.id}
                  studentId={student.id}
                  isExpanded={isExpanded}
                  isActive={isActive}
                  name={name}
                  pageNumbers={pageNumbers}
                  studentPages={studentPages}
                  onToggleExpand={() => {
                    setSelectedStudentId(student.id);
                    setExpandedStudentId((current) =>
                      current === student.id ? null : student.id,
                    );
                  }}
                  onOpenGallery={openGallery}
                  onRemove={handleUnassignPage}
                  isDragActive={!!activePageId || !!activeStudentThumbId}
                />
              );
            })}
          </BulkUploadStudentList>
        </BulkUploadStudentListPanel>
      </BulkUploadOrganiseLayout>

      <DragOverlay adjustScale={false} dropAnimation={null}>
        {activeDragPage ? (
          <PageCardVisual page={activeDragPage} isOverlay />
        ) : activeStudentThumbId ? (
          (() => {
            // Extract page ID from prefixed sortable ID
            // Format: "student-{studentId}-{pageId}"
            const id = String(activeStudentThumbId);
            let pageId;
            if (id.startsWith('student-')) {
              const parts = id.split('-');
              pageId = parts.slice(2).join('-'); // Handle page IDs that contain dashes
            } else {
              pageId = id;
            }

            // Find the active thumb page
            let thumbPage = null;
            Object.values(studentAssignments).forEach((pageIds) => {
              if (!thumbPage) {
                thumbPage = pages.find(p => p.id === pageId && pageIds.includes(pageId));
              }
            });
            return thumbPage ? <StudentThumbVisual page={thumbPage} isOverlay /> : null;
          })()
        ) : null}
      </DragOverlay>

      {galleryPageId && galleryPages.length > 0 && (
        <BulkUploadPageGallery
          pages={galleryPages}
          initialPageId={galleryPageId}
          onClose={closeGallery}
        />
      )}

      {previewPageId && previewPages.length > 0 && (
        <BulkUploadPageGallery
          pages={previewPages}
          initialPageId={previewPageId}
          onClose={closePagePreview}
        />
      )}

      {showUnassignedWarning && (
        <GeneralPopup
          title="Some pages are not assigned"
          textContent={unassignedWarningText(unassignedPages.length)}
          closeBtnText="Assign pages"
          buttonText="Continue anyway"
          icon={warningIcon}
          hidePopup={() => setShowUnassignedWarning(false)}
          confirmButtonAction={confirmDropUnassigned}
        />
      )}
    </DndContext>
    </BulkUploadWizardShell>
  );
};
